import type { FAQPage, WithContext } from 'schema-dts';

import type { FaqItem } from '@/app/components/sections/service/service-center/terms/types';
import { stripHtmlTags } from '@/app/utils/format';
import { reactNodeToSchemaHtml } from '@/app/utils/reactNodeToSchemaHtml';

export const getFaqItemSchemaFields = (item: FaqItem) => {
  const name = item.title ? stripHtmlTags(item.title) : '';

  if (typeof item.content === 'string') {
    return { name, answerText: stripHtmlTags(item.content) };
  }

  if (item.tableData) {
    const { rows, headers } = item.tableData;
    const answerText = rows
      .map((row) => {
        const textCell = row.cells.find((c) => c.type === 'text');
        const iconCells = row.cells.filter((c) => c.type === 'icon');
        const partName = stripHtmlTags((textCell?.content as string) ?? '');
        const coverage = iconCells
          .map(
            (c, i) =>
              `${stripHtmlTags(headers[i]?.label ?? '')}: ${c.label ?? ''}`
          )
          .join(', ');
        return `${partName} (${coverage})`;
      })
      .join('; ');
    return { name, answerText };
  }

  return { name, answerText: reactNodeToSchemaHtml(item.content) };
};

export interface BuildFaqJsonLdInput {
  url?: string | URL;
  name: string;
  items: FaqItem[];
  idSuffix?: string;
}

export const buildFaqJsonLd = (
  data: BuildFaqJsonLdInput
): WithContext<FAQPage> => {
  const name = data.name.trim() || 'FAQ';
  const url = data.url ? String(data.url) : '';
  const suffix = data.idSuffix?.trim() || 'faq';

  const mainEntity = (data.items ?? []).map((item, idx) => {
    const { name: questionName, answerText } = getFaqItemSchemaFields(item);
    return {
      '@type': 'Question' as const,
      '@id': `${url}#${suffix}-q${idx + 1}`,
      name: questionName,
      acceptedAnswer: {
        '@type': 'Answer' as const,
        '@id': `${url}#${suffix}-a${idx + 1}`,
        text: answerText,
      },
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: 'th-TH',
    '@id': `${url}#${suffix}`,
    mainEntityOfPage: url
      ? { '@type': 'WebPage' as const, '@id': `${url}#webpage` }
      : undefined,
    name,
    mainEntity,
  };
};
